<template>
  <div class="gs-fulfillment-step">
    <section class="gs-wizard-card gs-fulfillment-step__summary">
      <div class="gs-fulfillment-step__head">
        <div>
          <h2>Delivery by price point</h2>
          <p>{{ $t('frontend_product_step8_configure') }}</p>
        </div>
        <div class="gs-fulfillment-step__health">
          <strong>{{ configuredFulfillmentCount }}/{{ pricePoints.length }}</strong>
          <span>price points configured</span>
        </div>
      </div>
      <div class="gs-fulfillment-step__stats">
        <div class="gs-fulfillment-step__stat">
          <strong>{{ membershipCount }}</strong>
          <span>Membership accesses</span>
        </div>
        <div class="gs-fulfillment-step__stat">
          <strong>{{ integrationCount }}</strong>
          <span>{{ $t('frontend_product_step8_integrations') }}</span>
        </div>
        <div class="gs-fulfillment-step__stat">
          <strong>{{ fileCount }}</strong>
          <span>{{ $t('frontend_product_step8_files') }}</span>
        </div>
        <div class="gs-fulfillment-step__stat">
          <strong>{{ webhookCount }}</strong>
          <span>Webhooks</span>
        </div>
      </div>
      <div class="gs-fulfillment-step__list-head">
        <strong>Price points</strong>
        <span>Configure exactly what buyers receive for each pricing option.</span>
      </div>
      <div class="gs-fulfillment-step__accordions">
        <price-point-groups :price-points="pricePoints" v-slot="{ pricePoint, index }">
        <article
          :key="pricePoint.id || index"
          class="gs-fulfillment-step__accordion"
          :class="{ 'is-open': expandedFulfillmentIndex === index, 'is-configured': isFulfillmentConfigured(pricePoint) }"
        >
          <button type="button" class="gs-fulfillment-step__accordion-head" @click="toggleFulfillmentAccordion(index)">
            <span class="gs-fulfillment-step__status-dot"></span>
            <span class="gs-fulfillment-step__title-block">
              <span class="gs-fulfillment-step__name" :title="pricePoint.name">{{ pricePoint.name }}</span>
              <span class="gs-fulfillment-step__summary-line" :title="fulfillmentSummary(pricePoint)">{{ fulfillmentSummary(pricePoint) }}</span>
            </span>
            <span class="gs-fulfillment-step__meta">
              <span class="gs-fulfillment-step__pill">{{ fulfillmentCount(pricePoint, 'memberships') }} membership{{ fulfillmentCount(pricePoint, 'memberships') === 1 ? '' : 's' }}</span>
              <span class="gs-fulfillment-step__pill">{{ fulfillmentCount(pricePoint, 'post_sale_integrations') }} integration{{ fulfillmentCount(pricePoint, 'post_sale_integrations') === 1 ? '' : 's' }}</span>
              <span class="gs-fulfillment-step__pill">{{ fulfillmentCount(pricePoint, 'file_memberships') }} file{{ fulfillmentCount(pricePoint, 'file_memberships') === 1 ? '' : 's' }}</span>
              <span class="gs-fulfillment-step__pill">{{ fulfillmentCount(pricePoint, 'post_sale_details') }} webhook{{ fulfillmentCount(pricePoint, 'post_sale_details') === 1 ? '' : 's' }}</span>
            </span>
            <span class="gs-fulfillment-step__chevron">
              <gs-icon :name="expandedFulfillmentIndex === index ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
            </span>
          </button>
          <div v-if="expandedFulfillmentIndex === index" class="gs-fulfillment-step__accordion-body">
            <div class="gs-fulfillment-step__body-head">
              <div>
                <strong>{{ isFulfillmentConfigured(pricePoint) ? 'Delivery configured' : 'No delivery configured yet' }}</strong>
                <p>Choose what buyers receive immediately after purchasing this price point.</p>
              </div>
              <div class="gs-fulfillment-step__actions">
                <gs-button type="button" variant="outline" @click="openFulfillmentDrawer('membership', index)">Add membership</gs-button>
                <gs-button type="button" variant="outline" @click="openFulfillmentDrawer('integration', index)">Add integration</gs-button>
                <gs-button type="button" variant="outline" @click="openFulfillmentDrawer('file', index)">Add file</gs-button>
                <gs-button type="button" variant="outline" @click="openFulfillmentDrawer('webhook', index)">Add webhook</gs-button>
              </div>
            </div>
            <p class="gs-fulfillment-step__summary-line" role="status">{{ fulfillmentCount(pricePoint, 'file_memberships') }} download(s); {{ fulfillmentCount(pricePoint, 'memberships') ? 'membership access is included. Review welcome email settings in GrooveMember.' : 'no GrooveMember login or welcome email is created.' }}</p>
            <div class="gs-fulfillment-step__details">
              <fulfillment-summary title="Membership access" :items="membershipLabels(pricePoint)" :price-point-name="pricePoint.name" empty-text="No membership access has been added." />
              <fulfillment-summary title="Integration actions" :items="integrationLabels(pricePoint)" :price-point-name="pricePoint.name" empty-text="No post-sale integration runs for this price point." />
              <fulfillment-summary title="Downloadable files" :items="fileLabels(pricePoint)" :price-point-name="pricePoint.name" empty-text="No downloadable files have been attached." />
              <fulfillment-summary title="Webhooks" :items="webhookLabels(pricePoint)" :price-point-name="pricePoint.name" empty-text="No webhook URLs will be called after purchase." concealed />
            </div>
          </div>
        </article>
        </price-point-groups>
      </div>
    </section>

    <gs-drawer
      :value="fulfillmentDrawerOpen"
      size="xl"
      :title="fulfillmentDrawerTitle"
      :subtitle="selectedPricePoint ? selectedPricePoint.name : ''"
      @input="value => { if (!value) closeFulfillmentDrawer(); }"
    >
    <div class="gs-fulfillment-step__drawer">
      <div class="gs-fulfillment-step__drawer-body">
        <template v-if="fulfillmentDrawerMode === 'membership'">
          <div class="gs-fulfillment-step__drawer-field">
            <label>{{ $t('frontend_select') }} {{ $t('frontend_membership') }}</label>
            <gs-name-select
              v-model="postMembershipId"
              maxHeight="220px"
              size="full"
              :options="postMembershipsOptions"
              :placeholder="`${$t('frontend_select')} ${$t('frontend_membership')}`"
              @change="selectMembership($event)"
            />
          </div>
          <div class="gs-fulfillment-step__drawer-field">
            <label>{{ $t('frontend_select') }} {{ $t('frontend_access_level') }}</label>
            <gs-name-select
              v-model="postMembershipAccessLevelId"
              maxHeight="220px"
              size="full"
              :options="postAccessLevelsOptions"
              :placeholder="`${$t('frontend_select')} ${$t('frontend_access_level')}`"
              @change="postaccesslevelid($event)"
            />
          </div>
          <div class="gs-fulfillment-step__drawer-list" v-if="selectedPricePoint && membershipLabels(selectedPricePoint).length">
            <strong>Current membership access</strong>
            <span v-for="(label, membershipIndex) in membershipLabels(selectedPricePoint)" :key="label">
              <span class="gs-fulfillment-step__drawer-item-label">{{ label }}</span>
              <gs-button type="button" variant="danger" size="sm" @click="deleteFulfillmentItem('memberships', selectedPricePointIndex, membershipIndex)">Delete</gs-button>
            </span>
          </div>
        </template>

        <template v-if="fulfillmentDrawerMode === 'integration'">
          <div class="gs-fulfillment-step__drawer-field">
            <label>Fulfillment type</label>
            <gd-dropdown
              :value="integrationType"
              :options="integrationTypeOptions"
              size="full"
              maxHeight="220px"
              @change="setValue($event)"
              @keyup="setValue($event)"
            />
          </div>
          <config-menu
            v-if="integrationType && integrationType != ''"
            :key="configMenuKey"
            :initial-values="componentConfigValues"
            :plugin-id="pluginId"
            :resources-path="resourcesPath"
            :server-path="serverPath"
            :plugin="integrationType"
            :app-data="pluginSiteData"
            layout="vertical"
            :is-dark="false"
            :show-button-borders="false"
            :show-notifications-on-save="false"
            :opened-from="'groovesell'"
            :btn-label="$t('frontend_sites_integration_modal_add_integration')"
            @update="addPostSaleIntegration"
            @menu-updated="configMenuItemsUpdated"
          ></config-menu>
          <div class="gs-fulfillment-step__drawer-list" v-if="selectedPricePoint && integrationLabels(selectedPricePoint).length">
            <strong>Current integration actions</strong>
            <span v-for="(label, integrationIndex) in integrationLabels(selectedPricePoint)" :key="label">
              <span class="gs-fulfillment-step__drawer-item-label">{{ label }}</span>
              <gs-button type="button" variant="danger" size="sm" @click="deleteFulfillmentItem('post_sale_integrations', selectedPricePointIndex, integrationIndex)">Delete</gs-button>
            </span>
          </div>
        </template>

        <template v-if="fulfillmentDrawerMode === 'file'">
          <div class="gs-fulfillment-step__drawer-field">
            <label>{{ $t('frontend_select') }} {{ $t('frontend_file') }}</label>
            <gs-name-select
              v-model="postMembershipFileId"
              maxHeight="220px"
              size="full"
              :options="membershipFilesOptions"
              :placeholder="`${$t('frontend_select')} ${$t('frontend_file')}`"
              @change="post_addbtn = true"
            />
          </div>
          <div class="gs-fulfillment-step__drawer-list" v-if="selectedPricePoint && fileLabels(selectedPricePoint).length">
            <strong>Current downloadable files</strong>
            <span v-for="(label, fileIndex) in fileLabels(selectedPricePoint)" :key="label">
              <span class="gs-fulfillment-step__drawer-item-label">{{ label }}</span>
              <gs-button type="button" variant="danger" size="sm" @click="deleteFulfillmentItem('file_memberships', selectedPricePointIndex, fileIndex)">Delete</gs-button>
            </span>
          </div>
        </template>

        <template v-if="fulfillmentDrawerMode === 'webhook'">
          <div class="gs-fulfillment-step__drawer-field">
            <label>Webhook URL</label>
            <text-input
              v-model="webhookLink"
              :attribute="$t('frontend_product_step8_ipn_url')"
              value="webhookLink"
              :placeholder="$t('frontend_product_step8_ipn_url')"
            />
          </div>
          <div class="gs-fulfillment-step__drawer-list" v-if="selectedPricePoint && webhookLabels(selectedPricePoint).length">
            <strong>Current webhooks</strong>
            <span v-for="(label, webhookIndex) in webhookLabels(selectedPricePoint)" :key="label">
              <span class="gs-fulfillment-step__drawer-item-label">{{ label }}</span>
              <gs-button type="button" variant="danger" size="sm" @click="deleteWebhook(selectedPricePointIndex, webhookIndex)">Delete</gs-button>
            </span>
          </div>
        </template>
      </div>

      <div class="gs-fulfillment-step__drawer-footer">
        <gs-button type="button" variant="outline" @click="closeFulfillmentDrawer">Cancel</gs-button>
        <gs-button
          v-if="fulfillmentDrawerMode !== 'integration'"
          type="button"
          variant="primary"
          :disabled="!canSaveFulfillmentDrawer"
          @click="saveFulfillmentDrawer"
        >
          Add {{ fulfillmentDrawerNoun }}
        </gs-button>
      </div>
    </div>
    </gs-drawer>

    <gs-modal v-model="showPostSaleIntegrationModal" :title="$t('frontend_product_step5_add_new_integration')" size="xl">
      <div class="flex flex-col m-8">
        <div class="flex flex-col w-full mb-6">
          <div class="w-full flex flex-col mr-2">
            <label class="font-medium text-sm text-grey-700 mb-2">{{
              $t('frontend_product_step8_type')
            }}</label>
            <gd-dropdown :v-bind="integrationType" :value="integrationType" :options="integrationTypeOptions" size="full"
              maxHeight="150px" @change="setValue($event)" @keyup="setValue($event)" />
          </div>
          <div v-if="integrationType && integrationType != ''" class="w-full">
            <config-menu :key="configMenuKey" :initial-values="componentConfigValues" :plugin-id="pluginId"
              :resources-path="resourcesPath" :server-path="serverPath" :plugin="integrationType"
              :app-data="pluginSiteData" layout="vertical" :is-dark="false" :show-button-borders="false"
              :show-notifications-on-save="false" :opened-from="'groovesell'"
              :btn-label="$t('frontend_sites_integration_modal_add_integration')" @update="addPostSaleIntegration"
              @menu-updated="configMenuItemsUpdated"></config-menu>
          </div>
        </div>
      </div>
    </gs-modal>

    <gs-modal v-model="showMembershipModal"
      :title="isEditing === false ? $t('frontend_groovesell_add_membership') : $t('frontend_groovesell_edit_membership')"
      size="lg">
      <div class="flex flex-col px-4 h-96 justify-between">
        <div class="mt-4">
          <div class="-mt-4">
            <div class="flex flex-row w-full">
              <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_select') }}
                  {{ $t('frontend_membership') }}</label>
                <gs-name-select
                  v-model="postMembershipId"
                  maxHeight="150px"
                  size="full"
                  :options="postMembershipsOptions"
                  :placeholder="`${$t('frontend_select')} ${$t('frontend_membership')}`"
                  @change="selectMembership($event)"
                />
              </div>

              <div class="w-1/2 flex flex-col w-full mt-4">
                <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_select') }}
                  {{ $t('frontend_access_level') }}</label>
                <gs-name-select
                  v-model="postMembershipAccessLevelId"
                  maxHeight="150px"
                  size="full"
                  :options="postAccessLevelsOptions"
                  :placeholder="`${$t('frontend_select')} ${$t('frontend_access_level')}`"
                  @change="postaccesslevelid($event)"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row w-full mb-6 justify-end">
          <gs-button
            v-if="isEditing == false"
            variant="primary"
            :disabled="!post_addbtn"
            class="mt-4"
            @click="postdata != '' ? addMembershipField() : null"
          >
            {{ $t('frontend_add') }}
          </gs-button>
        </div>
      </div>
    </gs-modal>

    <gs-modal v-model="showMembershipFileModal"
      :title="isEditing === false ? $t('frontend_groovesell_add_new_file') : $t('frontend_groovesell_edit_file')"
      size="lg">
      <div class="flex flex-col px-4 h-96 justify-between">
        <div class="mt-4">
          <div class="-mt-4">
            <div class="flex flex-row w-full">
              <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_select') }}
                  {{ $t('frontend_membership') }}
                </label>
                <gs-name-select
                  v-model="postMembershipFileId"
                  maxHeight="150px"
                  size="full"
                  :options="membershipFilesOptions"
                  :placeholder="`${$t('frontend_select')} ${$t('frontend_file')}`"
                  @change="post_addbtn = true"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row w-full mb-6 justify-end">
          <gs-button
            v-if="isEditing == false"
            variant="primary"
            :disabled="!post_addbtn"
            class="mt-4"
            @click="addMembershipFile()"
          >
            {{ $t('frontend_add') }}
          </gs-button>
        </div>
      </div>
    </gs-modal>
  </div>
</template>

<script>
import { required, minLength, url } from 'vuelidate/lib/validators';
import ConfigMenu from '@groovepages/gd-ui-kit/components/PluginMenu';
import axios from 'axios';

import Memberships from './tabs/fullfillment/Memberships';
import Integrations from './tabs/fullfillment/Integrations';
import WebHook from './tabs/fullfillment/WebHook';
import Files from './tabs/fullfillment/Files';
import PricePointGroups from '../components/PricePointGroups.vue';
import FulfillmentSummary from '../components/FulfillmentSummary.vue';

export default {
  name: 'Step8',
  components: {
    ConfigMenu,
    Memberships,
    Integrations,
    WebHook,
    Files,
    PricePointGroups,
    FulfillmentSummary,
  },
  props: {
    model: {
      required: true,
    },
    resourcesPath: {
      required: true,
    },
    serverPath: {
      required: true,
    },
    pluginSiteData: {
      required: true,
    },
    availableIntegrations: {
      required: true,
    },
  },
  data() {
    return {
      localModel: this.model,
      fulfillmentTabs: [
        {
          title: 'Memberships',
          value: 'memberships',
        },
        {
          title: this.$t('frontend_product_step8_integrations'),
          value: 'integrations',
        },
        {
          title: this.$t('frontend_product_step8_webhook'),
          value: 'webHook',
        },
        {
          title: this.$t('frontend_product_step8_files'),
          value: 'files',
        },
      ],
      fulfillmentTab: 'memberships',
      showFulfillmentEditor: false,
      expandedFulfillmentIndex: 0,
      fulfillmentDrawerOpen: false,
      showPostSaleIntegrationModal: false,
      showMembershipModal: false,
      showMembershipFileModal: false,
      fulfillmentDrawerMode: '',
      pricePointsTab: '',
      contentAccessPricePointsTab: '',
      membershipPricePointsTab: '',
      filesPricePointsTab: '',
      integrationType: '',
      configMenuKey: 1,
      componentConfigValues: [],
      pluginId: '',
      integrationType: '',
      get_price_point_info: [],
      pluginMenuItems: [],
      selectedPricePointIndex: 0,
      post_memberships: [],
      membership_files: [],
      post_access_levels: [],
      pricingMemberships: [],
      post_addbtn: false,
      postdata: [],
      updating_index: '',
      membershipdata: '',
      postdataname: '',
      isEditing: false,
      widgetsTableColumns: ['membership', 'access_level', 'actions'],
      widgetsTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [],
        headings: {
          title: 'Membership',
          access_level: 'Access Level',
          expand: '',
        },
        filterByColumn: false,
        filterable: false,
        childRowTogglerFirst: false,
        perPage: 10000,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        },
      },
      postMembershipId: '',
      postMembershipFileId: '',
      postMembershipAccessLevelId: '',
      webhookLink: '',
      /* model: {
        postMembership: '',
        postaccesslevelid: ''
      } */
      filesTableColumns: ['name', 'actions'],
      filesTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [],
        headings: {
          title: 'Name',
          expand: '',
        },
        filterByColumn: false,
        filterable: false,
        childRowTogglerFirst: false,
        perPage: 10000,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        },
      },
    };
  },
  validations: {
    localModel: {
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          post_sale_details: {
            $each: {
              link: {
                url,
                required,
              },
            },
          },
        },
      },
    },
  },
  computed: {
    membershipCount() {
      return this.pricePoints.reduce((total, pricePoint) => {
        return total + ((pricePoint.memberships || []).length);
      }, 0);
    },
    integrationCount() {
      return this.pricePoints.reduce((total, pricePoint) => {
        return total + ((pricePoint.post_sale_integrations || []).length);
      }, 0);
    },
    fileCount() {
      return this.pricePoints.reduce((total, pricePoint) => {
        return total + ((pricePoint.file_memberships || []).length);
      }, 0);
    },
    webhookCount() {
      return this.pricePoints.reduce((total, pricePoint) => {
        return total + ((pricePoint.post_sale_details || []).length);
      }, 0);
    },
    configuredFulfillmentCount() {
      return this.pricePoints.filter((pricePoint) => {
        return this.isFulfillmentConfigured(pricePoint);
      }).length;
    },
    pricePoints() {
      return this.localModel.pricing_points || [];
    },
    contentAccessTypes() {
      return [
        {
          id: 0,
          title: this.$t('frontend_product_step8_no_access'),
          description: this.$t('frontend_product_step8_theres_no'),
        },
        {
          id: 1,
          title: this.$t('frontend_product_step8_a_downloadable'),
          description: this.$t('frontend_product_step8_a_groovesell', {appTitle: this.whitelabelAppData('sell').title}),
          coming_soon: true,
        },
        {
          id: 2,
          title: this.$t('frontend_product_step8_integrated'),
          description: this.$t('frontend_product_step8_the_actual'),
          coming_soon: true,
        },
      ];
    },
    contentAccessPricePointsTabs() {
      const pricePointTabs = [];
      for (let i = 0; i < this.pricePoints.length; i++) {
        pricePointTabs.push({
          title: this.pricePoints[i].name,
          value: `content_price_point_${this.pricePoints[i].id}`,
        });
      }

      return pricePointTabs;
    },
    gdButtonVariant() {
        return this.post_addbtn ? 'primary' : 'base';
    },
    postMembershipsOptions() {
      return this.post_memberships.map((membership) => {
        return {
          value: membership.id,
          text: membership.name,
        };
      });
    },
    postAccessLevelsOptions() {
      return this.post_access_levels.map((accessLevel) => {
        return {
          value: accessLevel.secret_key,
          text: accessLevel.name,
        };
      });
    },
    membershipFilesOptions() {
      return this.membership_files.map((file) => {
        return {
          value: file.id,
          text: file.name,
        };
      });
    },
    integrationTypeOptions() {
      return [
        {
          value: '',
          text: 'Please select fulfillment type',
        },
        ...this.availableIntegrations,
      ];
    },
    selectedPricePoint() {
      return this.pricePoints[this.selectedPricePointIndex] || null;
    },
    fulfillmentDrawerTitle() {
      const titles = {
        membership: 'Add membership access',
        integration: 'Add integration action',
        file: 'Add downloadable file',
        webhook: 'Add webhook URL',
      };

      return titles[this.fulfillmentDrawerMode] || 'Add fulfillment';
    },
    fulfillmentDrawerNoun() {
      const nouns = {
        membership: 'membership',
        integration: 'integration',
        file: 'file',
        webhook: 'webhook',
      };

      return nouns[this.fulfillmentDrawerMode] || 'item';
    },
    canSaveFulfillmentDrawer() {
      if (this.fulfillmentDrawerMode === 'membership') {
        return !!(this.postMembershipId && this.postMembershipAccessLevelId);
      }

      if (this.fulfillmentDrawerMode === 'file') {
        return !!this.postMembershipFileId;
      }

      if (this.fulfillmentDrawerMode === 'webhook') {
        return !!this.webhookLink;
      }

      return false;
    },
  },
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
  },
  async created() {
    const memberAppTitle = this.whitelabelAppData('member').url;
    const groovememberUrl = window.config.apiUrl.substr(
      0,
      window.config.apiUrl.lastIndexOf('/')
    );
    const result = await axios.get(
      `${groovememberUrl}${memberAppTitle}memberships`
    );
    this.post_memberships = result.data;
    const fileResult = await axios.get(
      `${groovememberUrl}${memberAppTitle}membership/file`
    );
    this.membership_files = fileResult.data.data;
  },
  async beforeMount() { },
  mounted() {
    if (this.contentAccessPricePointsTab == '' && this.pricePoints.length > 0) {
      this.contentAccessPricePointsTab = `content_price_point_${this.pricePoints[0].id}`;
    }
  },
  methods: {
    updateModel(val) {
      this.localModel = val
    },
    toggleFulfillmentAccordion(index) {
      this.expandedFulfillmentIndex = this.expandedFulfillmentIndex === index ? null : index;
    },
    fulfillmentCount(pricePoint, key) {
      return ((pricePoint && pricePoint[key]) || []).length;
    },
    isFulfillmentConfigured(pricePoint) {
      return ['memberships', 'post_sale_integrations', 'file_memberships', 'post_sale_details'].some((key) => {
        return this.fulfillmentCount(pricePoint, key) > 0;
      });
    },
    fulfillmentSummary(pricePoint) {
      const parts = [];

      if (this.fulfillmentCount(pricePoint, 'memberships')) {
        parts.push(`${this.fulfillmentCount(pricePoint, 'memberships')} membership access`);
      }

      if (this.fulfillmentCount(pricePoint, 'post_sale_integrations')) {
        parts.push(`${this.fulfillmentCount(pricePoint, 'post_sale_integrations')} integration action`);
      }

      if (this.fulfillmentCount(pricePoint, 'file_memberships')) {
        parts.push(`${this.fulfillmentCount(pricePoint, 'file_memberships')} downloadable file`);
      }

      if (this.fulfillmentCount(pricePoint, 'post_sale_details')) {
        parts.push(`${this.fulfillmentCount(pricePoint, 'post_sale_details')} webhook`);
      }

      return parts.length ? parts.join(' • ') : 'No delivery actions configured';
    },
    membershipLabels(pricePoint) {
      return ((pricePoint && pricePoint.memberships) || []).map((membership, index) => {
        const foundMembership = this.post_memberships.find((item) => {
          return item.id == membership.membership_id;
        });
        const foundAccessLevel = foundMembership && (foundMembership.access_level || []).find((level) => {
          return level.secret_key === membership.secret_key;
        });
        const membershipName = foundMembership ? foundMembership.name : `Membership ID ${membership.membership_id || index + 1}`;
        const accessName = foundAccessLevel ? foundAccessLevel.name : 'Access level selected';

        return `${membershipName} - ${accessName}`;
      });
    },
    fileLabels(pricePoint) {
      return ((pricePoint && pricePoint.file_memberships) || []).map((file, index) => {
        const foundFile = this.membership_files.find((item) => {
          return item.id == file.file_id;
        });

        return foundFile ? foundFile.name : `File ID ${file.file_id || index + 1}`;
      });
    },
    integrationLabels(pricePoint) {
      return ((pricePoint && pricePoint.post_sale_integrations) || []).map((integration, index) => {
        const platform = integration.platform || `Integration ${index + 1}`;
        const configuredFields = ((integration.config || []).map((field) => {
          return field.name || field.value;
        }).filter(Boolean));

        return configuredFields.length ? `${platform}: ${configuredFields.join(', ')}` : platform;
      });
    },
    webhookLabels(pricePoint) {
      return ((pricePoint && pricePoint.post_sale_details) || []).map((webhook, index) => {
        return webhook.link || `Webhook ${index + 1}`;
      });
    },
    openFulfillmentDrawer(mode, index) {
      this.selectedPricePointIndex = index;
      this.fulfillmentDrawerMode = mode;
      this.fulfillmentDrawerOpen = true;
      this.integrationType = '';
      this.pluginId = '';
      this.componentConfigValues = [];
      this.postMembershipId = '';
      this.postMembershipAccessLevelId = '';
      this.postMembershipFileId = '';
      this.webhookLink = '';
      this.post_access_levels = [];
      this.post_addbtn = false;
    },
    closeFulfillmentDrawer() {
      this.fulfillmentDrawerOpen = false;
      this.fulfillmentDrawerMode = '';
      this.webhookLink = '';
    },
    saveFulfillmentDrawer() {
      if (this.fulfillmentDrawerMode === 'membership') {
        this.addMembershipField();
      }

      if (this.fulfillmentDrawerMode === 'file') {
        this.addMembershipFile();
      }

      if (this.fulfillmentDrawerMode === 'webhook') {
        this.addWebhook(this.selectedPricePointIndex);
      }
    },
    addWebhook(index) {
      if (!this.webhookLink) {
        return;
      }

      if (!this.localModel.pricing_points[index].post_sale_details) {
        this.$set(this.localModel.pricing_points[index], 'post_sale_details', []);
      }

      this.localModel.pricing_points[index].post_sale_details.push({
        type: 1,
        link: this.webhookLink,
      });
      this.closeFulfillmentDrawer();
    },
    deleteWebhook(index, webhookIndex) {
      this.localModel.pricing_points[index].post_sale_details.splice(webhookIndex, 1);
    },
    deleteFulfillmentItem(key, pricePointIndex, itemIndex) {
      const collection = this.localModel.pricing_points[pricePointIndex] && this.localModel.pricing_points[pricePointIndex][key];

      if (Array.isArray(collection)) {
        collection.splice(itemIndex, 1);
      }
    },
    selectMembership(event) {
      this.model.postaccesslevelid = '';
      this.post_addbtn = false;
      this.postdata = [];
      const selectedPostMembership = this.post_memberships.find((ele) => {
        return ele.id == this.postMembershipId;
      });
      if (selectedPostMembership) {
        this.post_access_levels = selectedPostMembership.access_level;
      } else {
        this.post_access_levels = [];
      }
    },

    postaccesslevelid() {
      const selectedPostMembership = this.post_memberships.find((ele) => {
        return ele.id == this.postMembershipId;
      });
      const selectedAccessLevel = this.post_access_levels.find((ele) => {
        return ele.secret_key === this.postMembershipAccessLevelId;
      });
      if (selectedAccessLevel) {
        this.membershipdata = {
          membership_id: this.postMembershipId,
          secret_key: this.postMembershipAccessLevelId,
        };
        if (this.isEditing == false) {
          this.postdata.push(this.membershipdata);
        }

        this.post_addbtn = true;
      } else {
        this.postdataname = '';
        this.post_addbtn = false;
      }
    },

    showPostSaleIntegrationBox(index) {
      this.selectedPricePointIndex = index;
      this.integrationType = '';
      this.showPostSaleIntegrationModal = true;
    },
    handleShowPostSaleMembershipBox(index) {
      this.showPostSaleMembershipBox(index)
    },
    showPostSaleMembershipBox(index) {
      this.selectedPricePointIndex = index;
      this.integrationType = '';
      this.showMembershipModal = true;
    },
    showFileMembershipBox(index) {
      this.selectedPricePointIndex = index;
      this.integrationType = '';
      this.showMembershipFileModal = true;
    },

    setValue(value) {
      this.integrationType = value;
      this.pluginId = '';
      this.componentConfigValues = [];
      this.configMenuKey = this.configMenuKey + 1;
    },

    addMembershipField() {
      const completeMembershipInfo = {};
      completeMembershipInfo.membership_id = this.postMembershipId;
      completeMembershipInfo.secret_key = this.postMembershipAccessLevelId;

      if (
        this.localModel.pricing_points[this.selectedPricePointIndex].memberships
      ) {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].memberships.push(completeMembershipInfo);
      } else {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].memberships = [];
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].memberships.push(completeMembershipInfo);
      }

      this.showMembershipModal = false;
      this.closeFulfillmentDrawer();
    },

    addMembershipFile() {
      if (this.postMembershipFileId) {
        const completeMemberFileInfo = {};
        completeMemberFileInfo.file_id = this.postMembershipFileId;
        if (
          this.localModel.pricing_points[this.selectedPricePointIndex]
            .file_memberships
        ) {
          this.localModel.pricing_points[
            this.selectedPricePointIndex
          ].file_memberships.push(completeMemberFileInfo);
        } else {
          this.localModel.pricing_points[
            this.selectedPricePointIndex
          ].file_memberships = [];
          this.localModel.pricing_points[
            this.selectedPricePointIndex
          ].file_memberships.push(completeMemberFileInfo);
        }
        this.showMembershipFileModal = false;
        this.closeFulfillmentDrawer();
      }
    },
    addPostSaleIntegration(data) {
      const integrationConfig = data.config;
      const self = this;

      const integrationInfo = [];

      for (const index in integrationConfig) {
        const value = integrationConfig[index];

        if (value) {
          self.pluginMenuItems.forEach((menuValue, menuIndex) => {
            if (menuValue.key == index) {
              const { options } = menuValue;

              const objChecker = options.some((val) => {
                return typeof val === 'object';
              });

              let correspondingOption = value;
              if (objChecker) {
                correspondingOption = options.find((o) => o.value === value);
              }

              const singleIntegrationField = {};
              singleIntegrationField.index = index;
              singleIntegrationField.value = value;
              singleIntegrationField.label = menuValue.label;

              if (objChecker) {
                singleIntegrationField.name = correspondingOption.text;
              } else {
                singleIntegrationField.name = value;
              }

              integrationInfo.push(singleIntegrationField);
            }
          });
        }
      }

      const completeIntegrationInfo = {};
      completeIntegrationInfo.platform = this.integrationType.split('-')[0];
      completeIntegrationInfo.config = integrationInfo;

      if (
        this.localModel.pricing_points[this.selectedPricePointIndex]
          .post_sale_integrations
      ) {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].post_sale_integrations.push(completeIntegrationInfo);
      } else {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].post_sale_integrations = [];
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].post_sale_integrations.push(completeIntegrationInfo);
      }

      this.showPostSaleIntegrationModal = false;
      this.closeFulfillmentDrawer();
    },

    configMenuItemsUpdated(data) {
      this.pluginMenuItems = data.menuItems;
    },

    getHex() {
      let rgb = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(`--vs-${this.color}`);
      rgb = rgb.split(',');
      return `#${(
        (1 << 24) +
        (Number(rgb[0]) << 16) +
        (Number(rgb[1]) << 8) +
        Number(rgb[2])
      )
        .toString(16)
        .slice(1)}`;
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    handleFulfillmentTabChange(newTab) {
      this.fulfillmentTab = newTab;
    },

    handleContentAccessPricePointsTabChange(newTab) {
      this.contentAccessPricePointsTab = newTab;
    },

  },
};
</script>
<style scoped>
.gs-fulfillment-step {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: var(--gs-foreground);
}


.gs-fulfillment-step__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.gs-fulfillment-step__head h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-fulfillment-step__head p {
  margin: 6px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  line-height: 1.4;
}

.gs-fulfillment-step__health {
  min-width: 170px;
  padding: 13px 16px;
  text-align: center;
  background: var(--gs-success-soft);
  border: 1px solid var(--gs-success-soft);
  border-radius: var(--gs-radius-md);
}

.gs-fulfillment-step__health strong {
  display: block;
  color: var(--gs-success);
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
}

.gs-fulfillment-step__health span {
  display: block;
  margin-top: 6px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.gs-fulfillment-step__stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.gs-fulfillment-step__stat {
  min-height: 78px;
  padding: 16px;
  text-align: left;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-fulfillment-step__stat strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
}

.gs-fulfillment-step__stat span {
  display: block;
  margin-top: 8px;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  font-weight: 500;
}

.gs-fulfillment-step__list-head {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: baseline;
  column-gap: 18px;
  margin: 22px 0 10px;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gs-fulfillment-step__list-head strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-fulfillment-step__list-head span {
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.35;
}

.gs-fulfillment-step__accordions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gs-fulfillment-step__accordion {
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  overflow: hidden;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.gs-fulfillment-step__accordion.is-configured {
  border-left: 4px solid var(--gs-success);
}

.gs-fulfillment-step__accordion.is-open {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-fulfillment-step__accordion-head {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto 24px;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 70px;
  padding: 10px 16px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  background: #ffffff;
  border: 0;
  cursor: pointer;
}

.gs-fulfillment-step__status-dot {
  width: 12px;
  height: 12px;
  border: 2px solid var(--gs-border);
  border-radius: 999px;
}

.gs-fulfillment-step__accordion.is-configured .gs-fulfillment-step__status-dot {
  background: var(--gs-success);
  border-color: var(--gs-success);
}

.gs-fulfillment-step__title-block {
  min-width: 0;
  text-align: left;
}

.gs-fulfillment-step__name {
  display: block;
  min-width: 0;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-fulfillment-step__summary-line {
  display: block;
  margin-top: 5px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-fulfillment-step__meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

.gs-fulfillment-step__pill {
  padding: 8px 10px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
  white-space: nowrap;
}

.gs-fulfillment-step__chevron {
  color: var(--gs-muted-foreground);
  font-size: 20px;
  text-align: right;
}

.gs-fulfillment-step__accordion-body {
  padding: 18px 16px 18px 46px;
  border-top: 1px solid var(--gs-border);
}

.gs-fulfillment-step__body-head {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-bottom: 16px;
}

.gs-fulfillment-step__body-head strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-fulfillment-step__body-head p {
  margin: 5px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.4;
}

.gs-fulfillment-step__actions {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
}

.gs-fulfillment-step__details {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.gs-fulfillment-step__drawer {
  position: static;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: visible;
  color: var(--gs-foreground);
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

.gs-fulfillment-step__drawer-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 18px;
}

.gs-fulfillment-step__drawer-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gs-fulfillment-step__drawer-field label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
}

.gs-fulfillment-step__drawer-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-fulfillment-step__drawer-list strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-fulfillment-step__drawer-list > span {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 10px 12px;
  color: var(--gs-foreground);
  font-size: 14px;
  overflow-wrap: anywhere;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-fulfillment-step__drawer-item-label {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.gs-fulfillment-step__drawer-list > span > .gs-button {
  flex-shrink: 0;
}

.gs-fulfillment-step__drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px 0 0;
  border-top: 1px solid var(--gs-border);
  margin-top: 24px;
}

@media (max-width: 980px) {
  .gs-fulfillment-step__head,
  .gs-fulfillment-step__body-head {
    display: block;
  }

  .gs-fulfillment-step__health {
    margin-top: 14px;
  }

  .gs-fulfillment-step__stats,
  .gs-fulfillment-step__details {
    grid-template-columns: 1fr;
  }

  .gs-fulfillment-step__accordion-head {
    grid-template-columns: 18px minmax(0, 1fr) 24px;
  }

  .gs-fulfillment-step__meta {
    display: none;
  }

  .gs-fulfillment-step__actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 14px;
  }
}

@media (max-width: 640px) {
  .gs-fulfillment-step__accordion-body {
    padding: 16px;
  }

  .gs-fulfillment-step__list-head {
    display: block;
  }

  .gs-fulfillment-step__list-head span {
    display: block;
    margin-top: 6px;
  }

  .gs-fulfillment-step__actions {
    grid-template-columns: 1fr;
  }

  .gs-fulfillment-step__drawer {
    top: 0;
    border-radius: 0;
  }

  .gs-fulfillment-step__drawer-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
}
</style>
